// The pieces every score is drawn with: the state pill, the status dot, the
// tick bar, the plain bar, and the rule for which colour a pace figure gets.
//
// The score cards themselves live on the screens now — the operator home draws
// one row per flowline with a tick bar, the leader's overview the same rows
// for End-of-Line QC — and both read their colours from here, so a line cannot
// be two colours depending on which screen is looking.

import type { CSSProperties, ReactNode } from 'react';
import { paceTone, tickBar } from '@/lib/pace';

// `accent` is the live tone — a pod that is here to be worked, or in hand. It
// is the one tone drawn from the accent pack rather than the base palette, so
// it follows whichever accent the operator picked, as the started border does.
export type Tone = 'pos' | 'warn' | 'danger' | 'neutral' | 'accent';

const TONE_INK: Record<Tone, string> = {
  pos: 'var(--pos)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  neutral: 'var(--sub)',
  accent: 'var(--accent)',
};
const TONE_BG: Record<Tone, string> = {
  pos: 'var(--posSoft)',
  warn: 'var(--warnSoft)',
  danger: 'var(--dangerSoft)',
  neutral: 'var(--surface2)',
  accent: 'var(--soft)',
};

/**
 * A tinted state pill — the pace chip, the starvation chip, the badges.
 *
 * `solid` fills it with the tone instead of tinting it, for the two or three
 * states in a list that must be seen before anything else is read. A tint is
 * not enough for that: the muted accent pack's tint is all but the neutral
 * grey, so a tinted "ready" chip read as "not here yet" on the default theme.
 */
export function StatChip({
  tone = 'neutral',
  size = 'md',
  solid = false,
  style,
  children,
}: {
  tone?: Tone;
  size?: 'sm' | 'md';
  solid?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        borderRadius: 999,
        padding: size === 'sm' ? '2px 8px' : '4px 10px',
        font: `700 ${size === 'sm' ? 10 : 11}px Manrope,sans-serif`,
        background: solid ? TONE_INK[tone] : TONE_BG[tone],
        // The accent pack says what sits on its own colour; the base tones are
        // all mid-strength, so the page background is the ink that reads on
        // them in both themes.
        color: solid
          ? tone === 'accent'
            ? 'var(--accentInk)'
            : 'var(--bg)'
          : tone === 'neutral'
            ? 'var(--sub)'
            : TONE_INK[tone],
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/** The status dot that opens most rows in the design. */
export function Dot({ tone, size = 9, grey = false }: { tone: Tone; size?: number; grey?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: grey ? 'var(--line)' : tone === 'neutral' ? 'var(--accent)' : TONE_INK[tone],
        flex: 'none',
      }}
    />
  );
}

/**
 * The tick bar: one segment per pod of the day's number, marking what is done,
 * where pace says you should be, and what is still to go. See `tickBar` for
 * why the marker is drawn over the tick it lands on rather than behind it.
 *
 * `doneTone` colours the finished segments — green on pace, red behind — so
 * the bar and the chip beside it never disagree. Past `max` the bar stops
 * being a count and becomes a proportion.
 */
export function TickBar({
  done,
  target,
  dueNow,
  height = 8,
  gap = 3,
  max = 24,
  doneTone = 'pos',
}: {
  done: number;
  target: number | null;
  dueNow: number | null;
  height?: number;
  gap?: number;
  max?: number;
  doneTone?: 'pos' | 'danger' | 'grey';
}) {
  const ticks = tickBar(done, target, dueNow, max);
  if (ticks.length === 0) return null;
  const doneColour = doneTone === 'pos' ? 'var(--pos)' : doneTone === 'danger' ? 'var(--danger)' : 'var(--line)';
  return (
    <span
      aria-hidden="true"
      style={{ display: 'grid', gridTemplateColumns: `repeat(${ticks.length},1fr)`, gap, flex: 1, minWidth: 0 }}
    >
      {ticks.map((t, i) => (
        <span
          key={i}
          style={{
            height,
            borderRadius: 3,
            background: t === 'done' ? doneColour : t === 'now' ? 'var(--ink)' : 'var(--surface2)',
            ...(t === 'now' ? { boxShadow: '0 0 0 2px var(--surface2)' } : {}),
          }}
        />
      ))}
    </span>
  );
}

/** Which colour a flowline's PACE CHIP carries — neutral when exactly on pace. */
export function lineTone(line: { delta: number | null }): Tone {
  const t = paceTone(line.delta);
  return t === 'pos' ? 'pos' : t === 'danger' ? 'danger' : 'neutral';
}

/**
 * Which colour a flowline's BAR carries, on every screen that draws one.
 *
 * A bar shows work done, so on pace is green. Grey would read as "nothing is
 * happening on this line", which is the opposite of what on-pace means. On
 * pace is `delta >= 0`, measured against a floored pace line (see `dueByNow`),
 * so a line holding five against a pace of 5.6 is green — in the same place
 * the marker on the bar stands.
 */
export function barTone(line: { delta: number | null }): 'pos' | 'danger' {
  return (line.delta ?? 0) < 0 ? 'danger' : 'pos';
}

/**
 * The day's score on a phone: done over target, a plain sentence, and the pace
 * chip. "Pace says 12 by 14:10 · 7 to go".
 *
 * It is the first row of the flowline card rather than a card of its own,
 * because it is the total of the rows under it and nothing else — a separate
 * card read as a second thing to check, and pushed the pod to work next below
 * the fold on the phones the floor actually carries. A tablet carries the same
 * figure in the header instead and does not draw this.
 */
export function ScoreRow({
  done,
  target,
  sentence,
  chip,
  chipTone,
}: {
  done: number;
  target: number | null;
  sentence: string;
  chip: string;
  chipTone: Tone;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0 10px' }}>
      <span className="numeric" style={{ font: "700 28px/1 'Space Grotesk',sans-serif" }}>
        {done}
        {target != null && <span style={{ color: 'var(--sub)', fontSize: 15 }}>/{target}</span>}
      </span>
      <span style={{ flex: 1, minWidth: 0, font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', lineHeight: 1.35 }}>
        {sentence}
      </span>
      <StatChip tone={chipTone}>{chip}</StatChip>
    </div>
  );
}

/**
 * One flowline's row: its short name, the tick bar, done over target, the pace
 * chip, and a word on the right for whatever is wrong. The operator home and
 * the leader's overview both draw it.
 */
export function FlowlineRow({
  short,
  done,
  target,
  dueNow,
  delta,
  chip,
  chipTone,
  exText,
  exTone,
  onClick,
  small = false,
}: {
  short: string;
  done: number;
  target: number | null;
  dueNow: number | null;
  delta: number | null;
  chip: string;
  chipTone: Tone;
  exText: string;
  exTone: Tone | 'grey';
  onClick?: () => void;
  small?: boolean;
}) {
  const body = (
    <>
      <span style={{ width: small ? 30 : 34, flex: 'none', font: `700 ${small ? 10.5 : 13}px 'Space Grotesk',sans-serif`, color: small ? 'var(--sub)' : 'var(--ink)' }}>
        {short}
      </span>
      <TickBar
        done={done}
        target={target ?? 1}
        dueNow={dueNow}
        height={small ? 6 : 8}
        gap={small ? 2 : 3}
        doneTone={exTone === 'grey' ? 'grey' : barTone({ delta })}
      />
      <span
        className="numeric"
        style={{ font: `600 ${small ? 11 : 12}px Manrope,sans-serif`, color: 'var(--sub)', width: small ? 34 : 40, textAlign: 'right', flex: 'none' }}
      >
        {target == null ? done : `${done}/${target}`}
      </span>
      {!small && (
        <StatChip tone={chipTone} size="sm" style={{ width: 58, justifyContent: 'center', padding: '2px 0' }}>
          {chip}
        </StatChip>
      )}
      <span
        style={{
          width: small ? 64 : 96,
          flex: 'none',
          font: `${small ? 600 : 500} ${small ? 10 : 11}px Manrope,sans-serif`,
          color: exTone === 'grey' ? 'var(--sub)' : exTone === 'neutral' ? 'var(--sub)' : TONE_INK[exTone],
          textAlign: 'right',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {exText}
      </span>
    </>
  );
  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: small ? 8 : 10,
    minHeight: small ? 22 : 44,
    textAlign: 'left',
    width: '100%',
    ...(small ? {} : { borderTop: '1px solid var(--surface2)' }),
  };
  return onClick ? (
    <button type="button" className="press-row" onClick={onClick} style={style}>
      {body}
    </button>
  ) : (
    <div style={style}>{body}</div>
  );
}
